/**
 * Month projection card, shared by both cloud tabs.
 *
 * AWS passes the Cost Explorer forecast with its 80% prediction interval; GCP
 * passes a run-rate estimate with no interval and a badge that says so — the
 * two must never look equally authoritative.
 */

import { useTranslation } from 'react-i18next';
import { TrendingUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { DeltaBadge, EmptyState, deltaOf, fmtDay, fmtMonth, fmtUSD } from './shared';

export function ForecastCard({
  period,
  projected,
  mtdActual,
  priorMonthActual,
  interval,
  badge,
  snapshotDate,
  stale,
  note,
  emptyDescription,
}: {
  period?: string;
  projected?: number;
  mtdActual?: number;
  priorMonthActual?: number;
  /** AWS only — the 80% prediction interval, as whole-month totals (shown as-is). */
  interval?: { lower: number; upper: number };
  badge: string;
  /** AWS only — when the snapshot was taken, so a stale forecast reads as stale. */
  snapshotDate?: string;
  /** AWS only — the snapshot is for another month or older than the daily cron explains. */
  stale?: boolean;
  note?: string;
  emptyDescription?: string;
}) {
  const { t } = useTranslation();

  if (period == null || projected == null) {
    return (
      <EmptyState
        title={t('finops.cloud.forecast_empty_title')}
        description={emptyDescription ?? t('finops.cloud.forecast_empty_description')}
      />
    );
  }

  const vsPrior = deltaOf(projected, priorMonthActual ?? 0);

  return (
    <Card className="p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            {t('finops.cloud.forecast_title')}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">{fmtMonth(period)}</p>
        </div>
        <span className="rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground whitespace-nowrap">
          {badge}
        </span>
      </div>

      <div>
        <div className="text-3xl font-bold">{fmtUSD(projected)}</div>
        <div className="text-xs mt-1">
          <DeltaBadge value={vsPrior} />{' '}
          <span className="text-muted-foreground">{t('finops.cloud.forecast_vs_prior')}</span>
        </div>
        {interval && (
          <p className="text-xs text-muted-foreground mt-1.5">
            {t('finops.cloud.forecast_interval', {
              lower: fmtUSD(interval.lower),
              upper: fmtUSD(interval.upper),
            })}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 pt-1 border-t">
        <div className="pt-3">
          <div className="text-xs text-muted-foreground">{t('finops.cloud.forecast_mtd')}</div>
          <div className="text-sm font-semibold mt-0.5">{fmtUSD(mtdActual)}</div>
        </div>
        <div className="pt-3">
          <div className="text-xs text-muted-foreground">{t('finops.cloud.forecast_prior_month')}</div>
          <div className="text-sm font-semibold mt-0.5">{fmtUSD(priorMonthActual)}</div>
        </div>
      </div>

      {(note || snapshotDate) && (
        <p className={`text-[11px] ${stale ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}`}>
          {note}
          {note && snapshotDate ? ' · ' : ''}
          {snapshotDate
            ? t(stale ? 'finops.cloud.forecast_snapshot_stale' : 'finops.cloud.forecast_snapshot', { date: fmtDay(snapshotDate) })
            : ''}
        </p>
      )}
    </Card>
  );
}
